import { sendPreRequestAuditIfMatched } from '@/utils/audit/sendPreRequestAudit';
import type { FinalizeUploadRequestOptions } from '../../types';

export const finalizeUploadRequest = async <T>({
  url,
  method,
  token,
  signal,
  body,
  onProgress,
}: FinalizeUploadRequestOptions): Promise<T> => {
  if (!url) {
    throw new Error('finalizeUploadRequest requires url');
  }

  await sendPreRequestAuditIfMatched(url, {
    method: method ?? 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body ?? {}),
  });

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method ?? 'POST', `${process.env.NEXT_PUBLIC_API_URL}${url}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.responseType = 'json';

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress((event.loaded / event.total) * 100);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as T);
      } else if (xhr.status === 401) {
        const error = new Error(`Finalize failed with status ${xhr.status}`) as Error & { status: number };
        error.status = 401;
        reject(error);
      } else {
        const error = new Error(`Finalize failed with status ${xhr.status}`) as Error & { status: number };
        error.status = xhr.status;
        reject(error);
      }
    };

    xhr.onerror = () => reject(new Error('Upload error'));
    xhr.onabort = () => reject(new Error('Upload canceled'));
    signal?.addEventListener('abort', () => xhr.abort());

    xhr.send(JSON.stringify(body ?? {}));
  });
};
